import { useEffect, useId, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { MODAL_INPUT_CLASS, ModalFooter, ModalHeader, ModalInput, ModalTextarea } from '@/components/admin/editorial-modal';
import { Button } from '@/components/ui/button';
import { ChipRadioGroup } from '@/components/ui/chip-radio-group';
import { Activity, Check, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { labelFor } from '@/i18n/labels';
import { SYMPTOM_SEVERITY_LABEL_KEY, SYMPTOM_SEVERITY_TOKENS, SYMPTOM_TYPE_LABEL_KEY } from '@/lib/medical-display';
import { useCompanionName } from '@/hooks/useCompanionName';
import type { Medication, SymptomEntry } from '@/types/api';

const SEVERITIES: SymptomEntry['severity'][] = ['mild', 'moderate', 'severe'];
const TYPES: SymptomEntry['type'][] = [
  'symptom', 'medication_reaction', 'treatment_reaction', 'improvement', 'other',
];

interface LogSymptomModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  medications: Medication[];
  prefill?: { type?: SymptomEntry['type']; linked_medication_id?: string };
  /** When set, the modal hydrates from this record and submits an update.
   *  Every field is editable — including `reported_to_doctor`, which is what
   *  clears the severe-unreported banner once the parent has told the care
   *  team (#847 P1: before this, the flag was writable only at create). */
  editing?: SymptomEntry | null;
  onSubmit: (data: Partial<SymptomEntry>) => void;
  isSubmitting?: boolean;
}

const todayDate = () => new Date().toISOString().slice(0, 10);

const baseEmpty: Partial<SymptomEntry> = {
  date: '',
  type: 'symptom',
  description: '',
  severity: 'moderate',
  linked_medication_id: '',
  what_helped: '',
  reported_to_doctor: false,
  doctor_response: '',
};

export function LogSymptomModal({
  open,
  onOpenChange,
  medications,
  prefill,
  editing,
  onSubmit,
  isSubmitting,
}: LogSymptomModalProps) {
  const { t } = useTranslation(['health', 'common']);
  const companionName = useCompanionName();
  const [form, setForm] = useState<Partial<SymptomEntry>>({ ...baseEmpty, date: todayDate() });
  const isEdit = Boolean(editing);
  const severityLabelId = useId();

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        date: editing.date ? editing.date.slice(0, 10) : todayDate(),
        type: editing.type,
        description: editing.description,
        severity: editing.severity,
        linked_medication_id: editing.linked_medication_id ?? '',
        what_helped: editing.what_helped ?? '',
        reported_to_doctor: editing.reported_to_doctor,
        doctor_response: editing.doctor_response ?? '',
      });
    } else {
      setForm({
        ...baseEmpty,
        date: todayDate(),
        ...(prefill?.type ? { type: prefill.type } : {}),
        ...(prefill?.linked_medication_id ? { linked_medication_id: prefill.linked_medication_id } : {}),
      });
    }
  }, [open, prefill?.type, prefill?.linked_medication_id, editing]);

  const valid = (form.description?.trim() ?? '').length > 0 && form.date && form.severity && form.type;

  // #2417: a reaction to a drug the child has stopped must still be linkable,
  // so inactive medications are offered too, in their own "Inactive" group
  // after the active ones. (An edit's existing inactive link lands there.)
  const activeMeds = medications.filter((m) => m.active);
  const inactiveMeds = medications.filter((m) => !m.active);
  const medOption = (m: Medication) => (
    <option key={m._id} value={m._id}>
      {m.medication_name}{m.dose ? ` · ${m.dose}` : ''}
    </option>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl flex flex-col p-0 gap-0 max-h-[90vh]">
        <ModalHeader
          icon={Activity}
          eyebrow={isEdit ? t('health:modal.symptom.editEyebrow') : t('health:modal.symptom.newEyebrow')}
          title={isEdit ? t('health:modal.symptom.editTitle') : t('health:modal.symptom.newTitle')}
        />

        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-5 min-h-0">
          <FieldGroup label={t('health:modal.symptom.field.severity')} labelId={severityLabelId}>
            <ChipRadioGroup
              labelledBy={severityLabelId}
              value={form.severity ?? 'moderate'}
              onValueChange={(sev) => setForm((f) => ({ ...f, severity: sev }))}
              chipClassName="justify-center gap-2 text-body"
              markerClassName="h-2.5 w-2.5"
              options={SEVERITIES.map((sev) => ({
                value: sev,
                label: labelFor(SYMPTOM_SEVERITY_LABEL_KEY, sev, t),
                markerClassName: SYMPTOM_SEVERITY_TOKENS[sev].marker,
              }))}
            />
          </FieldGroup>

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
            <Field label={t('health:modal.symptom.field.type')}>
              <select
                value={form.type ?? 'symptom'}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as SymptomEntry['type'] }))}
                className={MODAL_INPUT_CLASS}
              >
                {TYPES.map((ty) => (
                  <option key={ty} value={ty}>{labelFor(SYMPTOM_TYPE_LABEL_KEY, ty, t)}</option>
                ))}
              </select>
            </Field>
            <Field label={t('health:modal.symptom.field.when')}>
              <ModalInput
                type="date"
                value={form.date ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              />
            </Field>
          </div>

          <Field label={t('health:modal.symptom.field.description')}>
            <ModalTextarea
              value={form.description ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={t('health:modal.symptom.placeholder.description')}
              rows={3}
            />
          </Field>

          {(form.type === 'medication_reaction' || medications.length > 0) && (
            <Field label={t('health:modal.symptom.field.linkedMedication')}>
              <select
                value={form.linked_medication_id ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, linked_medication_id: e.target.value }))}
                className={MODAL_INPUT_CLASS}
              >
                <option value="">{t('common:answers.none')}</option>
                {activeMeds.map(medOption)}
                {inactiveMeds.length > 0 && (
                  <optgroup label={t('health:chrome.scope.inactive')}>
                    {inactiveMeds.map(medOption)}
                  </optgroup>
                )}
              </select>
            </Field>
          )}

          <Field label={t('health:modal.symptom.field.whatHelped')}>
            <ModalTextarea
              value={form.what_helped ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, what_helped: e.target.value }))}
              placeholder={t('health:modal.symptom.placeholder.whatHelped')}
              rows={2}
            />
          </Field>

          <label className="inline-flex items-center gap-2 cursor-pointer text-body">
            <input
              type="checkbox"
              checked={form.reported_to_doctor ?? false}
              onChange={(e) => setForm((f) => ({ ...f, reported_to_doctor: e.target.checked }))}
              className="h-4 w-4 rounded border-input text-stevens-indigo"
            />
            <span>{t('health:modal.symptom.reportedToCareTeam')}</span>
          </label>

          {form.reported_to_doctor && (
            <Field label={t('health:modal.symptom.field.doctorResponse')}>
              <ModalTextarea
                value={form.doctor_response ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, doctor_response: e.target.value }))}
                placeholder={t('health:modal.symptom.placeholder.doctorResponse', { companion: companionName })}
                rows={2}
              />
            </Field>
          )}
        </div>

        <ModalFooter
          wrap
          lead={
            <span className="text-label uppercase text-stevens-ink-3">
              {form.severity === 'severe' && !form.reported_to_doctor
                ? t('health:modal.symptom.footerFlagSevere', { companion: companionName })
                : t('health:modal.symptom.footerSaved')}
            </span>
          }
        >
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            size="sm"
            variant="brand"
            disabled={!valid || isSubmitting}
            onClick={() => onSubmit(form)}
          >
            {isEdit ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
            {isSubmitting ? t('common:states.saving') : isEdit ? t('common:actions.saveChanges') : t('health:modal.symptom.submit')}
          </Button>
        </ModalFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-meta font-medium uppercase text-stevens-ink-3 mb-1.5 block">
        {label}
      </span>
      {children}
    </label>
  );
}

/** A heading over a chip group: a span the group points at with
 *  aria-labelledby, never a wrapping <label> (#2434). */
function FieldGroup({ label, labelId, children }: { label: string; labelId: string; children: React.ReactNode }) {
  return (
    <div>
      <span id={labelId} className="text-meta font-medium uppercase text-stevens-ink-3 mb-1.5 block">
        {label}
      </span>
      {children}
    </div>
  );
}
